import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Calendar, Clock, User, Building2, MapPin, CheckCircle2, XCircle,
  AlertTriangle, RefreshCw, ChevronRight, Home, ArrowLeft, Eye,
  MessageSquare, Send, CalendarCheck, CalendarX, Timer, Phone
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import api from '@/services/api';

// ============ CONSTANTS ============

const VIEWING_STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-gold-600', textColor: 'text-gold-400', icon: Clock },
  approved: { label: 'Approved', color: 'bg-green-600', textColor: 'text-green-400', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-600', textColor: 'text-red-400', icon: XCircle },
  completed: { label: 'Completed', color: 'bg-blue-600', textColor: 'text-blue-400', icon: CalendarCheck },
  cancelled: { label: 'Cancelled', color: 'bg-gray-600', textColor: 'text-gray-400', icon: CalendarX },
  no_show: { label: 'No Show', color: 'bg-red-700', textColor: 'text-red-400', icon: AlertTriangle },
};

const TIME_SLOTS = [
  '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'
];

// ============ HELPER FUNCTIONS ============

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-AE', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const formatTime = (timeStr) => {
  if (!timeStr) return 'N/A';
  return timeStr;
};

const formatDateTime = (dateStr, timeStr) => {
  return `${formatDate(dateStr)} at ${formatTime(timeStr)}`;
};

const formatRelativeTime = (ts) => {
  if (!ts) return 'N/A';
  const date = new Date(ts);
  const now = new Date();
  const diff = now - date;
  
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  
  return formatDate(ts);
};

const formatCurrency = (amount) => {
  if (!amount) return 'AED 0';
  return `AED ${Number(amount).toLocaleString()}`;
};

// ============ COMPONENTS ============

const ViewingStatusBadge = ({ status }) => {
  const config = VIEWING_STATUS_CONFIG[status] || VIEWING_STATUS_CONFIG.pending;
  const Icon = config.icon;
  
  return (
    <Badge className={`${config.color} text-white px-2 py-1`}>
      <Icon className="w-3 h-3 mr-1" />
      {config.label}
    </Badge>
  );
};

const ViewingCard = ({ viewing, userRole, onAction, onViewDetails }) => {
  const config = VIEWING_STATUS_CONFIG[viewing.status] || VIEWING_STATUS_CONFIG.pending;
  const isPending = viewing.status === 'pending';
  const isApproved = viewing.status === 'approved';
  const isConfirmedByBuyer = !!viewing.confirmed_by_buyer_at;
  
  return (
    <Card className="border-white/10 hover:border-white/30 transition-all" data-testid={`viewing-card-${viewing.id}`}>
      <CardContent className="p-4">
        <div className="flex gap-4">
          {/* Property Image */}
          <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden">
            {viewing.property?.images?.[0] ? (
              <img 
                src={viewing.property.images[0]} 
                alt={viewing.property?.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-white/5 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white/20" />
              </div>
            )}
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div>
                <h3 className="font-semibold truncate">
                  {viewing.property?.title || 'Property Viewing'}
                </h3>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {viewing.property?.location || 'Dubai'}
                </p>
              </div>
              <ViewingStatusBadge status={viewing.status} />
            </div>
            
            {/* Schedule Info */}
            <div className="flex items-center gap-4 text-sm mb-3">
              {viewing.confirmed_date ? (
                <div className="flex items-center gap-1 text-green-400">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDateTime(viewing.confirmed_date, viewing.confirmed_time)}</span>
                </div>
              ) : (
                <div className="flex items-center gap-1 text-gold-400">
                  <Calendar className="w-4 h-4" />
                  <span>Requested: {formatDate(viewing.preferred_date)} ({viewing.preferred_time})</span>
                </div>
              )}
            </div>
            
            {/* Buyer/Seller Info */}
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
              <User className="w-3 h-3" />
              {userRole === 'seller' ? (
                <span>Buyer: {viewing.buyer?.full_name || 'Buyer'}</span>
              ) : (
                <span>Seller: {viewing.seller?.full_name || 'Seller'}</span>
              )}
              {viewing.buyer_kyc_verified && (
                <Badge variant="outline" className="text-[10px] border-green-600/50 text-green-400">
                  KYC Verified
                </Badge>
              )}
              {isApproved && isConfirmedByBuyer && (
                <Badge variant="outline" className="text-[10px] border-blue-600/50 text-blue-400">
                  Confirmed
                </Badge>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex items-center gap-2">
              {userRole === 'seller' && isPending && (
                <>
                  <Button 
                    size="sm" 
                    onClick={() => onAction('approve', viewing)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Approve
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => onAction('reject', viewing)}
                  >
                    <XCircle className="w-3 h-3 mr-1" />
                    Reject
                  </Button>
                </>
              )}
              
              {userRole === 'buyer' && isApproved && !isConfirmedByBuyer && (
                <Button 
                  size="sm" 
                  onClick={() => onAction('confirm', viewing)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Confirm Viewing
                </Button>
              )}
              
              {userRole === 'seller' && isApproved && isConfirmedByBuyer && (
                <>
                  <Button 
                    size="sm" 
                    onClick={() => onAction('complete', viewing)}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <CalendarCheck className="w-3 h-3 mr-1" />
                    Mark Complete
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => onAction('noshow', viewing)}
                  >
                    No Show
                  </Button>
                </>
              )}
              
              {(isPending || isApproved) && (
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={() => onAction('cancel', viewing)}
                >
                  Cancel
                </Button>
              )}
              
              <Button 
                size="sm" 
                variant="ghost"
                onClick={() => onViewDetails(viewing)}
                className="ml-auto"
              >
                Details
                <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </div>
        </div>
        
        {/* Notes */}
        {viewing.notes && (
          <div className="mt-3 pt-3 border-t border-white/10">
            <p className="text-xs text-muted-foreground">
              <MessageSquare className="w-3 h-3 inline mr-1" />
              {viewing.notes}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const RequestViewingModal = ({ open, onClose, property, onSubmit, loading }) => {
  const [form, setForm] = useState({
    preferred_date: '',
    preferred_time: '10:00',
    alternative_date: '',
    alternative_time: '14:00',
    notes: ''
  });
  
  const handleSubmit = () => {
    if (!form.preferred_date || !form.preferred_time) {
      toast.error('Please select a preferred date and time');
      return;
    }
    onSubmit(form);
  };
  
  // Get minimum date (tomorrow)
  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);
  const minDateStr = minDate.toISOString().split('T')[0];
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-background border-white/10">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-primary" />
            Request Viewing
          </DialogTitle>
          <DialogDescription>
            Schedule a viewing for {property?.title || 'this property'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Preferred Date *</label>
              <Input 
                type="date"
                value={form.preferred_date}
                onChange={(e) => setForm({ ...form, preferred_date: e.target.value })}
                min={minDateStr}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Preferred Time *</label>
              <Select value={form.preferred_time} onValueChange={(v) => setForm({ ...form, preferred_time: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((time) => (
                    <SelectItem key={time} value={time}>{time}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <Separator />
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Alternative Date</label>
              <Input 
                type="date"
                value={form.alternative_date}
                onChange={(e) => setForm({ ...form, alternative_date: e.target.value })}
                min={minDateStr}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Alternative Time</label>
              <Select value={form.alternative_time} onValueChange={(v) => setForm({ ...form, alternative_time: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((time) => (
                    <SelectItem key={time} value={time}>{time}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Notes for Seller</label>
            <Textarea 
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Any special requests or questions for the seller..."
              rows={3}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Submit Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ApproveViewingModal = ({ open, onClose, viewing, onSubmit, loading }) => {
  const [form, setForm] = useState({
    confirmed_date: viewing?.preferred_date || '',
    confirmed_time: viewing?.preferred_time || '10:00'
  });
  
  const handleSubmit = () => {
    if (!form.confirmed_date || !form.confirmed_time) {
      toast.error('Please confirm date and time');
      return;
    }
    onSubmit(form);
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[400px] bg-background border-white/10">
        <DialogHeader>
          <DialogTitle>Approve Viewing</DialogTitle>
          <DialogDescription>
            Confirm the viewing schedule for the buyer
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="p-3 rounded-lg bg-blue-950/20 border border-blue-600/30">
            <p className="text-sm text-blue-400">
              Buyer requested: {formatDateTime(viewing?.preferred_date, viewing?.preferred_time)}
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Confirm Date</label>
              <Input 
                type="date"
                value={form.confirmed_date}
                onChange={(e) => setForm({ ...form, confirmed_date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Confirm Time</label>
              <Select value={form.confirmed_time} onValueChange={(v) => setForm({ ...form, confirmed_time: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((time) => (
                    <SelectItem key={time} value={time}>{time}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-green-600 hover:bg-green-700">
            {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RejectViewingModal = ({ open, onClose, onSubmit, loading }) => {
  const [reason, setReason] = useState('');
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[400px] bg-background border-white/10">
        <DialogHeader>
          <DialogTitle>Reject Viewing</DialogTitle>
          <DialogDescription>
            Provide a reason for rejecting this viewing request
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <Textarea 
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason for rejection..."
            rows={3}
          />
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSubmit(reason)} disabled={loading} variant="destructive">
            {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
            Reject
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ MAIN COMPONENT ============

const ViewingsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userRole = user?.role?.toLowerCase();
  
  const [viewings, setViewings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal states
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedViewing, setSelectedViewing] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Fetch viewings
  const fetchViewings = useCallback(async () => {
    try {
      setRefreshing(true);
      
      let response;
      if (userRole === 'seller' || userRole === 'seller_broker') {
        response = await api.get('/seller/viewings');
        setViewings(response.data?.viewings || []);
      } else {
        response = await api.get('/buyer/viewings');
        setViewings(response.data?.viewings || []);
      }
    } catch (error) {
      toast.error('Failed to load viewings');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [userRole]);
  
  useEffect(() => {
    fetchViewings();
  }, [fetchViewings]);
  
  // Filter viewings
  const filteredViewings = viewings.filter(v => {
    if (statusFilter === 'all') return true;
    return v.status === statusFilter;
  });
  
  // Stats
  const stats = {
    total: viewings.length,
    pending: viewings.filter(v => v.status === 'pending').length,
    approved: viewings.filter(v => v.status === 'approved').length,
    completed: viewings.filter(v => v.status === 'completed').length
  };
  
  // Actions
  const handleAction = async (action, viewing) => {
    setSelectedViewing(viewing);
    
    switch (action) {
      case 'approve':
        setApproveModalOpen(true);
        break;
      case 'reject':
        setRejectModalOpen(true);
        break;
      case 'confirm':
        await handleConfirm(viewing);
        break;
      case 'complete':
        await handleComplete(viewing, 'completed');
        break;
      case 'noshow':
        await handleComplete(viewing, 'no_show');
        break;
      case 'cancel':
        await handleCancel(viewing);
        break;
      default:
        break;
    }
  };
  
  const handleApprove = async (form) => {
    setActionLoading(true);
    try {
      await api.post(`/seller/viewings/${selectedViewing.id}/respond`, {
        approved: true,
        confirmed_date: form.confirmed_date,
        confirmed_time: form.confirmed_time
      });
      toast.success('Viewing approved');
      setApproveModalOpen(false);
      fetchViewings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve viewing');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleReject = async (reason) => {
    setActionLoading(true);
    try {
      await api.post(`/seller/viewings/${selectedViewing.id}/respond`, {
        approved: false,
        rejection_reason: reason
      });
      toast.success('Viewing rejected');
      setRejectModalOpen(false);
      fetchViewings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject viewing');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleConfirm = async (viewing) => {
    try {
      await api.post(`/viewings/${viewing.id}/confirm`);
      toast.success('Viewing confirmed');
      fetchViewings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm viewing');
    }
  };
  
  const handleComplete = async (viewing, outcome) => {
    try {
      await api.post(`/viewings/${viewing.id}/complete?outcome=${outcome}`);
      toast.success(outcome === 'completed' ? 'Viewing completed' : 'Marked as no-show');
      fetchViewings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete viewing');
    }
  };
  
  const handleCancel = async (viewing) => {
    try {
      await api.post(`/viewings/${viewing.id}/cancel`);
      toast.success('Viewing cancelled');
      fetchViewings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel viewing');
    }
  };
  
  const handleViewDetails = (viewing) => {
    // Could open a details drawer or navigate
    toast.info(`Viewing ${viewing.id.slice(0, 8)} details`);
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading viewings...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background" data-testid="viewings-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold">
                  {userRole === 'seller' ? 'Viewing Requests' : 'My Viewings'}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {userRole === 'seller' ? 'Manage property viewing requests' : 'Your scheduled property viewings'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={fetchViewings}
                disabled={refreshing}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')}>
                <Home className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-blue-400" />
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-gold-600/30 bg-gold-950/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-gold-400" />
                <div>
                  <p className="text-2xl font-bold text-gold-400">{stats.pending}</p>
                  <p className="text-xs text-muted-foreground">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-green-600/30 bg-green-950/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
                <div>
                  <p className="text-2xl font-bold text-green-400">{stats.approved}</p>
                  <p className="text-xs text-muted-foreground">Approved</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-blue-600/30 bg-blue-950/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <CalendarCheck className="w-5 h-5 text-blue-400" />
                <div>
                  <p className="text-2xl font-bold text-blue-400">{stats.completed}</p>
                  <p className="text-xs text-muted-foreground">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Filter */}
        <div className="flex items-center gap-4 mb-6">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Viewings</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          
          <p className="text-sm text-muted-foreground">
            Showing {filteredViewings.length} viewings
          </p>
        </div>
        
        {/* Viewings List */}
        {filteredViewings.length > 0 ? (
          <div className="space-y-4">
            {filteredViewings.map((viewing) => (
              <ViewingCard 
                key={viewing.id}
                viewing={viewing}
                userRole={userRole}
                onAction={handleAction}
                onViewDetails={handleViewDetails}
              />
            ))}
          </div>
        ) : (
          <Card className="border-white/10">
            <CardContent className="py-16 text-center">
              <Calendar className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No Viewings Found</h3>
              <p className="text-sm text-muted-foreground">
                {userRole === 'seller' 
                  ? "You haven't received any viewing requests yet."
                  : "You haven't requested any property viewings yet."}
              </p>
              {userRole === 'buyer' && (
                <Button className="mt-4" onClick={() => navigate('/properties')}>
                  <Building2 className="w-4 h-4 mr-2" />
                  Browse Properties
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </main>
      
      {/* Modals */}
      <ApproveViewingModal 
        open={approveModalOpen}
        onClose={() => setApproveModalOpen(false)}
        viewing={selectedViewing}
        onSubmit={handleApprove}
        loading={actionLoading}
      />
      
      <RejectViewingModal 
        open={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        onSubmit={handleReject}
        loading={actionLoading}
      />
    </div>
  );
};

export default ViewingsPage;
export { RequestViewingModal };
